import ApiController from './ApiController';
import PermissaoController from './PermissaoController';
import Administrador from '../models/Administrador';

/**
 * Lista de administradores retornada pela API com informações de paginação
 * @typedef Listagem
 * @property {Boolean} sucesso Indica se a requisição foi executada
 * @property {Array<Erro>} erros Listagem dos erros retornados
 * @property {Array<Administrador>} items Em caso de sucesso contém os administradores retornados
 * @property {Number} page Número da página
 * @property {Number} count Quantidade total de registros
 * @property {Number} pageCount Quantidade de páginas disponíveis
 * @property {Number} pageSize Quantidade de registros por página
 */

/**
 * Retorno dos métodos
 * @typedef Retorno
 * @property {Boolean} sucesso Indica se o método foi executado com êxito
 * @property {Administrador} administrador Dados do administrador
 */

/**
 * Retorno da listagem sem paginação
 * @typedef RetornoSelecao
 * @property {Boolean} sucesso Indica se a requisição foi executada
 * @property {Array<Administrador>} items Em caso de sucesso contém os administradores retornados
 */

/**
 * Controller para cadastro de administradores
 */
class AdministradorController {

    constructor() {
        this.apiController = new ApiController();
        this.permissaoController = new PermissaoController();
    }

    /**
     * Lista os administradores cadastrados
     * @param {Number} page Número da página
     * @param {Number} pageSize Quantidade de registros por página
     * @param {Object} params Parâmetros extras de pesquisa
     * @param {String} params.nome Pesquisar um administrador por nome
     * 
     * @return {Promise<RetornoListar>} Retorna os administradores encontrados
     */
    async listar(page, pageSize, { nome }) {
        try {
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: result } = await this.apiController.get('/administrador', { page, pageSize, nome });
            //Marca que houve retorno com sucesso
            result.sucesso = true;
            //Mapeia os itens para objetos do tipo Administrador
            const items = result.items.map(item => Administrador.clone(item));
            result.items = items;
            return result;
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            return { sucesso: false, erros: error.response.data };
        }
    }

    /**
     * Retorna os dados de um administrador específico
     * @param {Number} id Id do administrador a ser consultado
     * 
     * @return {Promise<Retorno>} Retorna o administrador solicitado
     */
    async obter(id) {
        //Verifica se o ID está preenchido
        if (!id)
            return { sucesso: false, erros: [ { campo: 'id', mensagem: 'É necessário' } ] };
        
        try {
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: result } = await this.apiController.get('/administrador/' + id.toString());
            //Retorna indicando que houve sucesso e os dados do administrador
            return { sucesso: true, administrador: Administrador.clone(result) };
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            return { sucesso: false, erros: error.response.data };
        }
    }

    /**
     * Inclui um novo administrador
     * @param {Administrador} administrador Dados do administrador a ser incluído
     * 
     * @returns {Promise<Retorno>} Retorna o administrador inserido, com as permissões preenchidas
     */
    async salvar(administrador) {
        const erros = await this._validarCampos(administrador, false);
            
        //Retorna os erros para a aplicação
        if (erros.length > 0)
            return { sucesso: false, erros };

        try {
            //Monta o corpo da requisição
            const body = await this._extrairCampos(administrador);
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: result } = await this.apiController.post('/administrador', body);
            //Retorna indicando sucesso e cria uma cópia da avaliação
            return { sucesso: true, administrador: Administrador.clone(result) };
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            return { sucesso: false, erros: error.response.data };
        }
    }

    /**
     * Atualiza os dados de um administrador
     * @param {Administrador} administrador Dados do administrador a ser incluído
     * 
     * @returns {Promise<Retorno>} Retorna o administrador atualizado, com as permissões preenchidas
     */
    async atualizar(administrador) {
        const erros = await this._validarCampos(administrador, true);
            
        if (erros.length > 0)
            return { sucesso: false, erros };

        try {
            //Monta o corpo da requisição
            const body = await this._extrairCampos(administrador);
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: result } = await this.apiController.put('/administrador/' + administrador.id.toString(), body);
            //Retorna indicando sucesso e cria uma cópia do administrador
            return { sucesso: true, administrador: Administrador.clone(result) };
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            return { sucesso: false, erros: error.response.data };
        }
    }

    async _validarCampos(administrador, atualizacao) {
        var erros = [];

        //Verifica se a empresa está sendo preenchida
        if (!administrador) {
            erros.push({ campo: '', mensagem: 'Administrador é necessário' });
        } else {
            //Verifica se o id está preenchido
            if (atualizacao && !administrador.id) 
                erros.push({ campo: 'id', mensagem: 'É necessário' });
            
            //Verifica se o nome está preenchido
            if (!administrador.nome)
                erros.push({ campo: 'nome', mensagem: 'É necessário' });
            
            //Verifica se o email ou telefone do usuário estão preenchidos
            if (!administrador.email && !administrador.telefone) {
                erros.push({ campo: 'email', mensagem: 'É necessário' });
                erros.push({ campo: 'telefone', mensagem: 'É necessário' });
            }
            
            //Verifica se a senha está preenchida
            if (!atualizacao && !administrador.senha) 
                erros.push({ campo: 'senha', mensagem: 'É necessário' });
            
            if (!administrador.permissoes)
                erros.push({ campo: 'permissoes', mensagem: 'É necessário' });
            else if (!Array.isArray(administrador.permissoes))
                erros.push({ campo: 'permissoes', mensagem: 'Precisa ser um array' });
        }

        return erros;
    }

    /**
     * Extrai as informações para o envio para a API
     * @param {Administrador} administrador Dados do administrador a ser inserido
     * @returns {Object} Retorna apenas os dados necessários para a requisição
     */
    async _extrairCampos(administrador) {
        const result = {
            nome: administrador.nome,
            permissoes: administrador.permissoes.map(permissao => permissao.id)
        };
        
        if (administrador.senha)
            result.senha = administrador.senha;
        if (administrador.email)
            result.email = administrador.email;
        if (administrador.telefone)
            result.telefone = administrador.telefone;

        return result;
    }

}

export default AdministradorController;